import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { FaTruck, FaCheckCircle, FaClock, FaSpinner } from "react-icons/fa";
import Truck from "../../assets/icons/Truck.png";
import { Link } from "react-router-dom";
import Api from "../../axiosConfig";
import { toast } from "react-toastify";

const Home = () => {
  const { user } = useAuth();
  const firstName = user?.firstName || "";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeDeliveries: 0,
    completedDeliveries: 0,
    pendingDeliveries: 0,
    totalDeliveries: 0,
  });
  const [recentDeliveries, setRecentDeliveries] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch deliveries
        const deliveriesResponse = await Api.get("/v1/deliveries/my-deliveries");

        const allDeliveries = deliveriesResponse.data.deliveries || [];
        if (Array.isArray(allDeliveries)) {
          // Store deliveries in localStorage
          localStorage.setItem("deliveries", JSON.stringify(allDeliveries));

          // Calculate stats from deliveries
          const calculatedStats = {
            activeDeliveries: allDeliveries.filter((d) =>
              ["assigned", "picked_up", "in_transit", "moving_to_pickup_location"].includes(d.status.toLowerCase())
            ).length,
            completedDeliveries: allDeliveries.filter(
              (d) => d.status.toLowerCase() === "delivered"
            ).length,
            pendingDeliveries: allDeliveries.filter(
              (d) => d.status.toLowerCase() === "pending"
            ).length,
            totalDeliveries: allDeliveries.length,
          };

          setStats(calculatedStats);

          // Sort deliveries by creation date (newest first) and take the 5 most recent
          const sortedDeliveries = [...allDeliveries]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5);

          setRecentDeliveries(sortedDeliveries);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statsCards = [
    {
      title: "Active Deliveries",
      value: stats.activeDeliveries,
      iconType: "image",
      iconSrc: Truck,
      bgColor: "bg-[#0066CC]",
    },
    {
      title: "Completed Deliveries",
      value: stats.completedDeliveries,
      iconType: "image",
      iconSrc: Truck,
      bgColor: "bg-[#0066CC]",
    },
    {
      title: "Pending Deliveries",
      value: stats.pendingDeliveries,
      iconType: "image",
      iconSrc: Truck,
      bgColor: "bg-[#0066CC]",
    },
    {
      title: "Total Deliveries",
      value: stats.totalDeliveries,
      iconType: "image",
      iconSrc: Truck,
      bgColor: "bg-[#0066CC]",
    },
  ];

  const renderIcon = (stat) => {
    if (stat.iconType === "image") {
      return <img src={stat.iconSrc} alt={stat.title} className="w-14" />;
    }
    const Icon = stat.icon;
    return <Icon className="w-10 text-white" />;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "assigned":
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-8">
      {/* Header with Welcome and Request Button */}
      <div className="flex justify-between flex-wrap items-center">
        <h1 className="text-2xl">
          Welcome <span className="text-primary-dark">{firstName}</span> !
        </h1>
        <Link
          to="/order-delivery"
          className="px-6 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-light transition-colors"
        >
          Make a Request
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <div
            key={index}
            className={`${stat.bgColor} rounded-lg p-6 text-white flex flex-col items-start`}
          >
            <div className="flex w-full gap-10 items-center justify-between">
              {loading ? (
                <FaSpinner className="w-14 h-14 animate-spin" />
              ) : (
                renderIcon(stat)
              )}
              <div className="text-5xl font-bold">
                {loading ? "-" : stat.value}
              </div>
            </div>
            <div className="mt-5">
              <div className="text-xl">{stat.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Requests History */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Deliveries</h2>
          <Link
            to="/deliveries"
            className="text-primary-dark hover:text-primary-light transition-colors text-sm"
          >
            View All →
          </Link>
        </div>
        <div className="bg-white rounded-lg overflow-hidden shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <FaSpinner className="w-6 h-6 animate-spin mx-auto text-primary-dark" />
                    </td>
                  </tr>
                ) : recentDeliveries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-4 text-sm text-gray-500 text-center"
                    >
                      No deliveries found
                    </td>
                  </tr>
                ) : (
                  recentDeliveries.map((delivery) => (
                    <tr
                      key={delivery._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{delivery.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">
                          {delivery.recipientName}
                        </div>
                        <div className="text-gray-500">
                          {delivery.recipientPhone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium capitalize">
                          {delivery.packageType}
                        </div>
                        <div className="text-gray-500 capitalize">
                          {delivery.packageSize}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            delivery.status
                          )}`}
                        >
                          {delivery.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(delivery.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Link
                          to={`/delivery/${delivery._id}`}
                          className="text-primary-dark hover:text-primary-light transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
